// ============================================================================
// Beacon — native contacts (your own CRM, on your own database). Sellers,
// agents, wholesalers, lenders, attorneys, contractors — linked to deals.
// Source of truth is Beacon's Postgres; GoHighLevel only ever gets a copy.
// Same dual-mode repo pattern: cloud `contacts` with a graceful local fallback.
// ============================================================================

import { getSupabase, isCloud } from "./supabase";

export type ContactType = "Seller" | "Agent" | "Wholesaler" | "Lender" | "Attorney" | "Contractor" | "Other";
export const CONTACT_TYPES: ContactType[] = ["Seller", "Agent", "Wholesaler", "Lender", "Attorney", "Contractor", "Other"];

export interface Contact {
  id: string;
  name: string;
  type: ContactType;
  company?: string;
  email?: string;
  phone?: string;
  notes?: string;
  dealIds: string[]; // deals this contact is attached to
  created_at?: string;
}

let counter = 0;
export function newContactId(): string {
  counter += 1;
  return "ct" + Date.now().toString(36) + counter.toString(36);
}

export function blankContact(): Contact {
  return { id: newContactId(), name: "", type: "Seller", company: "", email: "", phone: "", notes: "", dealIds: [] };
}

/** Contacts attached to a given deal. */
export function contactsForDeal(contacts: Contact[], dealId: string): Contact[] {
  return contacts.filter((c) => c.dealIds.includes(dealId));
}

// --- persistence (cloud `contacts` + local fallback) ------------------------
const LS = "beacon_contacts";
function localAll(): Contact[] {
  try {
    const raw = localStorage.getItem(LS);
    if (raw) return JSON.parse(raw) as Contact[];
  } catch { /* ignore */ }
  return [];
}
function localSave(all: Contact[]) {
  try { localStorage.setItem(LS, JSON.stringify(all)); } catch { /* ignore */ }
}

export async function listContacts(): Promise<Contact[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("contacts").select("*").order("created_at");
      if (error) throw error;
      if (data) return data as Contact[];
    } catch { /* fall through to local */ }
  }
  return localAll();
}

export async function saveContact(c: Contact): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("contacts").upsert(c);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  const all = localAll();
  const i = all.findIndex((x) => x.id === c.id);
  if (i >= 0) all[i] = c; else all.push(c);
  localSave(all);
}

export async function deleteContact(id: string): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("contacts").delete().eq("id", id);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  localSave(localAll().filter((x) => x.id !== id));
}
